import { useRef, useState } from 'react';
import { BellOff, BellRing, Camera, Check, LoaderCircle } from 'lucide-react';
import { api } from '../../api/client';
import type { Attachment } from '../../api/types';
import { useAuth } from '../../auth/AuthProvider';
import { useChat } from '../../chat/ChatProvider';
import { Avatar } from '../../components/Avatar';
import { Modal } from '../../components/Modal';

export function AccountModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { user, updateUser } = useAuth();
  const chat = useChat();
  const input = useRef<HTMLInputElement>(null);
  const [uploaded, setUploaded] = useState<Attachment | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');

  if (!open || !user) return null;

  const choose = async (file: File | undefined) => {
    if (!file) return;
    setPending(true);
    setError('');
    try {
      if (uploaded) await api.deleteAttachment(uploaded.deleteUrl);
      setUploaded(await api.uploadAvatar(file));
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Не удалось загрузить аватар');
    } finally {
      setPending(false);
    }
  };

  const close = async () => {
    if (uploaded) await api.deleteAttachment(uploaded.deleteUrl).catch(() => undefined);
    setUploaded(null);
    onClose();
  };

  const save = async () => {
    if (!uploaded) return;
    setPending(true);
    try {
      await api.setAvatar(uploaded.id);
      updateUser({ avatar: uploaded.previewUrl || uploaded.attachmentUrl });
      setUploaded(null);
      onClose();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Не удалось сохранить аватар');
    } finally {
      setPending(false);
    }
  };

  return (
    <Modal
      actions={
        <>
          <button className="button button--secondary" onClick={() => void close()} type="button">Отмена</button>
          <button className="button button--primary" disabled={!uploaded || pending} onClick={() => void save()} type="button">
            {pending ? <LoaderCircle className="spin" size={16} /> : null} Сохранить
          </button>
        </>
      }
      onClose={() => void close()}
      size="small"
      title="Настройки аккаунта"
    >
      <div className="account-settings">
        {uploaded ? (
          <img alt="Новый аватар" className="avatar avatar--preview" src={uploaded.previewUrl || uploaded.attachmentUrl} />
        ) : (
          <Avatar size="large" user={user} />
        )}
        <strong>{user.initial}</strong>
        <input accept="image/*" hidden onChange={(event) => void choose(event.target.files?.[0])} ref={input} type="file" />
        <button className="button button--secondary" disabled={pending} onClick={() => input.current?.click()} type="button">
          <Camera size={16} /> Выбрать изображение
        </button>
        {error ? <p className="form-error">{error}</p> : null}
        {chat.notificationPermission !== 'unsupported' ? (
          <div className="account-notifications">
            <div>
              <strong>Уведомления о сообщениях</strong>
              <small>Звук и системные уведомления браузера</small>
            </div>
            {chat.notificationPermission === 'granted' ? (
              <span className="account-notifications__status account-notifications__status--enabled"><Check size={15} /> Включены</span>
            ) : chat.notificationPermission === 'denied' ? (
              <span className="account-notifications__status" title="Разрешите уведомления для сайта в настройках браузера"><BellOff size={15} /> Заблокированы в браузере</span>
            ) : (
              <button className="button button--secondary" onClick={() => void chat.enableNotifications()} type="button"><BellRing size={15} /> Включить</button>
            )}
          </div>
        ) : null}
      </div>
    </Modal>
  );
}
